import { useRouter } from 'next/navigation'

import { formatLetterTime } from '@/utils/func'
import { transformToMarkdownLetterPlainText } from '../utils/MarkdownWithoutExtension'
import { EngagementMessage, EngagementScene } from '@/types/letter'
import useI18n from '@/hooks/useI18n'
import clsx from 'clsx'
import { withLocale } from '@/locales'

const isCommentScene = (scene?: EngagementScene): boolean => {
  return (
    scene === 'reply_comment' ||
    scene === 'like_comment' ||
    scene === 'comment_character' ||
    scene === 'comment_project'
  )
}

const getSceneDescriptionKey = (scene?: EngagementScene): string => {
  switch (scene) {
    case 'reply_comment':
      return 'replied to your comment'
    case 'like_comment':
      return 'liked your comment'
    case 'comment_character':
      return 'commented on your character'
    case 'comment_project':
      return 'commented on your world'
    case 'liked_work':
      return 'liked your work!'
    case 'favorited_work':
      return 'added your work to their favorites!'
    default:
      return ''
  }
}

const getValidEngagementId = (value: unknown): string => {
  if (value === undefined || value === null) return ''

  const id = String(value).trim()
  return id && id !== '0' ? id : ''
}

export type LetterMessageItem = {
  message_id?: string
  title?: string
  content?: string
  image?: string[] | null
  send_time?: number
  read_time?: number
  data?: EngagementMessage['data'] | null
}

export function LetterItem({
  item,
  handlePress,
  tab,
}: {
  item: LetterMessageItem
  handlePress?: () => void
  tab?: 'System' | 'Personal' | 'Engagement'
}) {
  const router = useRouter()
  const { t, lang } = useI18n()

  if (!item || !item.message_id) {
    return null
  }

  const isClickable = tab !== 'Engagement' && handlePress

  if (tab === 'Engagement') {
    const engagementItem = item as EngagementMessage
    const scene = engagementItem.data?.scene
    const isComment = isCommentScene(scene)
    const hasCommentContent = Boolean(isComment && engagementItem.content)

    const handleAvatarPress = () => {
      let userId = engagementItem.data?.uid?.toString()

      if (!userId && engagementItem.image?.[0]) {
        const match = engagementItem.image[0]?.match(/\/user\/avatar\/(\d+)/)
        if (match?.[1]) {
          userId = match[1]
        }
      }

      if (!userId) return
      router.push(withLocale(`/profile/${userId}`, lang))
    }

    const handleWorkImagePress = () => {
      const projectId = getValidEngagementId(engagementItem.data?.project_id)
      if (projectId) {
        router.push(withLocale(`/world/${projectId}`, lang))
        return
      }

      const storyId = getValidEngagementId(engagementItem.data?.story_id)
      const characterId =
        getValidEngagementId(engagementItem.data?.character_id) ||
        getValidEngagementId(engagementItem.data?.cid) ||
        storyId

      if (!characterId) return

      const routeCharId = storyId ? `story${characterId}` : String(characterId)

      router.push(withLocale(`/chat/${routeCharId}`, lang))
    }

    const handleCardPress = () => {
      if (!isCommentScene(scene)) return

      const projectId = getValidEngagementId(engagementItem.data?.project_id)
      const characterId =
        getValidEngagementId(engagementItem.data?.character_id) ||
        getValidEngagementId(engagementItem.data?.cid)
      const storyId = getValidEngagementId(engagementItem.data?.story_id)
      const commentId = engagementItem.data?.comment_id
      const rootId = engagementItem.data?.root_id

      const targetId = projectId || storyId || characterId
      if (!targetId) return

      const query = new URLSearchParams()
      if (commentId) {
        query.set('comment_id', String(commentId))
      }
      if (rootId && rootId !== '0') {
        query.set('root_id', String(rootId))
      }

      const basePath = projectId
        ? `/world/${projectId}/comments`
        : `/chat/${storyId ? `story${targetId}` : String(targetId)}/comments`
      const path = `${basePath}${query.toString() ? `?${query.toString()}` : ''}`

      router.push(withLocale(path, lang))
    }

    return (
      <div
        className="relative m-auto mb-3 flex w-[90%] items-start gap-3 self-center rounded-lg bg-transparent py-2"
        key={item?.message_id || Math.random()}
        onClick={handleCardPress}
      >
        {engagementItem?.image?.[0] && (
          <button
            type="button"
            className="h-[56px] w-[56px] flex-shrink-0 overflow-hidden rounded-full"
            onClick={(event) => {
              event.stopPropagation()
              handleAvatarPress()
            }}
          >
            <img
              src={engagementItem.image[0]}
              className="h-full w-full object-cover"
              alt=""
              style={{ backgroundColor: '#270707' }}
            />
          </button>
        )}

        <div className="flex flex-1 flex-col gap-1">
          <h3 className="mt-1 text-sm font-medium text-white">
            {transformToMarkdownLetterPlainText(engagementItem?.title || '')}
          </h3>

          <div
            className={clsx('text-sm text-white/50', {
              'line-clamp-1': hasCommentContent,
              'line-clamp-2': !hasCommentContent,
            })}
            dangerouslySetInnerHTML={{
              __html: transformToMarkdownLetterPlainText(
                t(
                  isComment
                    ? getSceneDescriptionKey(scene)
                    : engagementItem?.content || ''
                )
              ),
            }}
          />

          {hasCommentContent && (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="h-3 w-[2px] rounded-full bg-white/30" />
              <div className="line-clamp-1 text-sm text-white/90">
                {engagementItem.content}
              </div>
            </div>
          )}

          <div className="text-xs text-white/50">
            {engagementItem?.send_time
              ? formatLetterTime(engagementItem.send_time * 1000)
              : ''}
          </div>
        </div>

        {engagementItem?.image?.[1] && (
          <button
            type="button"
            className="h-[56px] w-[56px] flex-shrink-0 overflow-hidden rounded-lg"
            onClick={(event) => {
              event.stopPropagation()
              handleWorkImagePress()
            }}
          >
            <img
              src={engagementItem.image[1]}
              className="h-full w-full object-cover"
              alt=""
            />
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'relative m-auto mb-3 flex h-[100px] w-[90%] items-center justify-between gap-3 self-center rounded-lg border border-white/20 bg-white/5 p-4',
        isClickable && 'cursor-pointer hover:border-white/40 hover:bg-white/10'
      )}
      key={item?.message_id || Math.random()}
      onClick={isClickable ? handlePress : undefined}
    >
      {item?.image?.[0] && (
        <div className="h-[56px] w-[56px] overflow-hidden rounded-lg">
          <img src={item.image[0]} className="h-[100%] w-[100%]" alt="" />
        </div>
      )}
      <div className="flex h-[65px] flex-1 flex-col justify-between">
        <h3 className="pr-10 text-sm font-medium tracking-wide text-white">
          {item?.title || ''}
        </h3>

        <div
          className="line-clamp-2 max-w-[280px] text-left text-sm text-white/50"
          dangerouslySetInnerHTML={{
            __html: transformToMarkdownLetterPlainText(item?.content || ''),
          }}
        />
      </div>
      <div className="absolute right-3 top-1">
        <span className="text-xs text-white/50">
          {item?.send_time ? formatLetterTime(item.send_time * 1000) : ''}
        </span>
      </div>
      {item?.read_time === 0 && (
        <div className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-[#ff3b30]" />
      )}
    </div>
  )
}
